import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable, useDataTableContext } from 'components/DataTable'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { Spinner } from 'react-bootstrap'
import apiService from 'services/api'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import styled from 'styled-components'
import { ContactResponse } from 'models/contacts'
import dialogConfirm from 'components/dialogConfirm'
import to from 'await-to-js'
import { toast } from 'react-toastify'
import { INote } from 'models/contact_companies'
import NotesModal from '../../Modals/NotesModal'
import { usePaginationURLParams } from 'utils/usePaginationURLParams'
import { useAuth } from '../../../context/authentication/useAuth'
import { ActionButtons } from '../../Buttons/ActionButtons'

type SortableFields = (keyof ContactResponse | string)[]

export type DealsTableRef = {
  refetch: () => void
}

const sortableFields: SortableFields = []

const PAGE_SIZE = 100

export const NotesTable = forwardRef(
  (
    {
      setTotalRowsCount,
      chainId,
      contactCompanyId,
      enablePagination = true,
      disableUrlPagination = false,
    }: {
      setTotalRowsCount?: (count: number) => void
      enablePagination?: boolean
      disableUrlPagination?: boolean
      chainId: number | undefined
      contactCompanyId: number | undefined
    },
    ref
  ) => {
    const [noteToEdit, setNoteToEdit] = useState<INote>()
    const tableContext = useDataTableContext()
    const queryClient = useQueryClient()
    const {
      state: { sorting, tableKey },
      methods: { clearSelectedRows },
    } = useDataTableContext()

    const [pagination, setPagination] = disableUrlPagination
      ? useState({
          pageIndex: 0,
          pageSize: PAGE_SIZE,
        })
      : usePaginationURLParams(PAGE_SIZE)
    const api = apiService()
    const { user: me } = useAuth()

    const sortParams = useMemo(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const params: Record<string, any> = {}

      if (sorting?.length) {
        params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
      }

      return params
    }, [sorting])

    const filterAndSortParams = useMemo(
      () => ({
        ...sortParams,
      }),
      [sortParams]
    )

    const TABLE_QUERY_KEY = getTableQueryKey({
      tableKey: tableKey,
      filterParams: filterAndSortParams,
      page: pagination.pageIndex + 1,
    })

    // DATA FETCHING
    const { isLoading, data, isFetching, refetch } = useQuery({
      queryKey: TABLE_QUERY_KEY,
      queryFn: async ({ signal }) => {
        await queryClient.cancelQueries(TABLE_QUERY_KEY)
        clearSelectedRows()

        if (!chainId && !contactCompanyId)
          return {
            results: [],
            count: 0,
          }

        const res = await api.getNotes(
          chainId,
          contactCompanyId,
          {
            ...filterAndSortParams,
            page: pagination.pageIndex + 1,
          },
          signal
        )

        setTotalRowsCount?.(res.count)
        return res
      },
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5,
    })
    // END DATA FETCHING

    // Make refetch available to parent
    useImperativeHandle(
      ref,
      () =>
        ({
          refetch: () => refetch(),
        }) as DealsTableRef
    )

    const columns = [
      {
        id: 'user',
        header: 'User',
        accessorFn: (row) =>
          !row.user
            ? `${me?.company.name} User`
            : row.user?.first_name + ' ' + row.user?.last_name,
        size: 40,
      },
      {
        accessorKey: 'content',
        header: 'Note',
        cell: (info) => {
          return info.getValue()
        },
      },
      {
        accessorKey: 'created',
        header: 'Date Added',
        cell: (info) => {
          const date = info.getValue()
          if (!date) return '-'

          return new Date(date).toLocaleDateString()
        },
        size: 40,
      },
      {
        accessorKey: 'modified',
        header: 'Date Modified',
        cell: (info) => {
          const date = info.getValue()
          if (!date) return '-'

          return new Date(date).toLocaleDateString()
        },
        size: 40,
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 20,
        meta: {
          isEditable: false,
        },
        cell: (info) => {
          const note = info.row.original
          const noteId = note.id
          return (
            <ActionButtons
              className={'justify-start'}
              onDelete={() => handleNoteDelete(noteId)}
              onEdit={() => setNoteToEdit(note)}
            />
          )
        },
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ] satisfies ColumnDef<INote, any>[]

    const handleNoteDelete = async (noteId: number) => {
      if (
        await dialogConfirm(
          <>
            Are you sure you want to delete this <b>note</b>? This action cannot
            be undone.
          </>,
          'Delete Note?',
          '',
          { confirmLabel: 'Delete' }
        )
      ) {
        const [err] = await to(api.deleteNote(noteId))
        if (!err) {
          toast.success('Note deleted successfully')
          refetch()
        }
      }
    }

    useEffect(() => {
      setTotalRowsCount?.(data?.count ?? 0)
    }, [data?.count])

    if (isLoading || !data) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '500px',
          }}
        >
          <Spinner animation="border" />
        </div>
      )
    }

    return (
      <>
        <TableContainer>
          <DataTable
            key={tableContext.state.tableKey}
            autoLayout
            data={data?.results ?? []}
            columns={columns}
            sortableFields={sortableFields}
            virtualizeRows={false}
            isPaginationEnabled={enablePagination}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            expandColAccessor={'expanded-row' as any}
            paginationOptions={{
              pageCount: Math.ceil(data?.count / PAGE_SIZE),
              setPagination: setPagination,
              pagination: pagination,
              isPaginationLoading: isLoading,
            }}
          />
          <LoadingOverlay show={isFetching}>
            <Spinner />
          </LoadingOverlay>

          {!!noteToEdit && (
            <NotesModal
              chainId={chainId}
              contactCompanyId={contactCompanyId}
              show={!!noteToEdit}
              noteToEdit={noteToEdit}
              handleClose={() => {
                setNoteToEdit(undefined)
                refetch()
              }}
            />
          )}
        </TableContainer>
      </>
    )
  }
)

NotesTable.displayName = 'NotesTable'

const TableContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 1;
  overflow: hidden;
  max-height: 600px;
  width: 100%;
`

const LoadingOverlay = styled.div<{
  show: boolean
}>`
  display: ${({ show }) => (show ? 'flex' : 'none')};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(1px);
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  z-index: 30;
`

export const Title = styled.h1`
  font-size: 18px;
  line-height: 28px;
  color: #101828;
`
